import { isEscapeKeyClick } from '@withjoy/joykit/utils';
import { useRef, useCallback } from 'react';
import { useCatalogContext } from '../../../state/Catalog';
import { deriveTelemetryPage } from '../../../utils/deriveTelemetryPage';
import { useCatalogSearchBarContext } from '../CatalogSearchBar.provider';

export type UseSearchBarReturn = ReturnType<typeof useSearchBar>;

const useSearchBar = ({ onFocus, onBlur }: { onFocus?: () => void; onBlur?: () => void }) => {
  const {
    isOpen,
    handleOnChangeSearchQuery,
    handleOnClearSearchQuery,
    handleOnOpen: openSearchBar,
    handleOnClose: closeSearchBar,
    handleOnSearchBarFocused,
    handleOnSubmitSearch,
    searchQuery
  } = useCatalogSearchBarContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const { selectedCategory } = useCatalogContext();

  const telemetryPage = deriveTelemetryPage({ product: null, selectedCategory, view: 'searchBar' });

  const clearSearchText = useCallback(() => {
    handleOnClearSearchQuery({ source: 'button', page: telemetryPage, method: 'clearButton' });
  }, [handleOnClearSearchQuery, telemetryPage]);

  const handleOnKeyDown = useCallback<React.KeyboardEventHandler<HTMLInputElement>>(e => {
    if (isEscapeKeyClick(e.key)) {
      // We want to prevent search reset, have the users click the "clear" button instead
      // First key click will blur the input
      // Second key click will close the drawer
      e.preventDefault();
      e.currentTarget.blur();
    }
  }, []);

  const handleOnChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    e => {
      const input = e.currentTarget;
      handleOnChangeSearchQuery(input.value);
    },
    [handleOnChangeSearchQuery]
  );

  const handleOnSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const isKeyboardSubmit = e.nativeEvent instanceof KeyboardEvent;
      handleOnSubmitSearch({ page: telemetryPage, method: isKeyboardSubmit ? 'enterKey' : 'searchButton' });
    },
    [handleOnSubmitSearch, telemetryPage]
  );

  const handleOnOpen = useCallback(() => {
    openSearchBar({ source: 'searchIcon', page: telemetryPage });
  }, [openSearchBar, telemetryPage]);

  const handleOnClose = useCallback(
    (source: 'overlayClick' | 'cancelButton') => {
      closeSearchBar({ page: telemetryPage, source });
    },
    [closeSearchBar, telemetryPage]
  );

  const handleOnFocus = useCallback(() => {
    handleOnSearchBarFocused(telemetryPage);
    onFocus?.();
  }, [handleOnSearchBarFocused, telemetryPage, onFocus]);

  return {
    isSearchOpen: isOpen,
    handleOnChange,
    handleOnClose,
    handleOnFocus,
    handleOnBlur: onBlur,
    handleOnOpen,
    handleOnKeyDown,
    handleOnSubmit,
    clearSearchText,
    inputRef,
    searchText: searchQuery
  };
};

export { useSearchBar };
